import React, { Component } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/apiService';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfirmationNotification from '../common/ConfirmationNotification';
import FormField from './FormField';

class EntityForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      validated: false,
      saving: false,
      crmFields: [],
      recordDetail: {},
      showSuccessNotification: false,
    };
  }

  componentDidMount() {
    this.loadFields();
    
    const editCrmid = this.props.crmid || this.props.entity_info?.crmid || this.props.entity_info?.id;
    if (editCrmid) {
      this.loadRecordDetails();
    } else {
      this.setState({ loading: false });
    }
  }

  loadFields = async () => {
    const { module, onLogout } = this.props;
    
    this.setState({ loading: true });
    
    try {
      const authService = require('../../services/authService').default;
      const moduleFields = authService.getModuleFields(module);
      
      if (moduleFields && moduleFields.length > 0) {
        const formFields = moduleFields.filter(field => 
          (field.name || field.fieldname) !== 'id' && 
          (field.name || field.fieldname) !== 'createdtime' && 
          (field.name || field.fieldname) !== 'modifiedtime' &&
          (field.name || field.fieldname) !== 'modifiedby' &&
          (field.name || field.fieldname) !== 'created_user_id' &&
          !(field.name || field.fieldname)?.startsWith('cf_')
        );
        
        this.setState({ crmFields: formFields });
      } else {
        this.setState({ crmFields: [] });
      }
    } catch (error) {
      this.setState({ crmFields: [] });
    } finally {
      if (!this.props.crmid) {
        this.setState({ loading: false });
      }
    }
  };

  loadRecordDetails = async () => {
    const crmid = this.props.crmid || this.props.entity_info?.crmid || this.props.entity_info?.id;
    const { onLogout } = this.props;
    
    if (!crmid) {
      this.setState({ loading: false });
      return;
    }
    
    this.setState({ loading: true });
    
    try {
      const result = await apiService.getRecordDetails(crmid);
      
      if (result.success) {
        const recordData = result.data.details || result.data.recordDetail || result.data;
        
        this.setState({
          recordDetail: recordData,
          loading: false,
        });
      } else if (result.needsLogout && onLogout) {
        onLogout();
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      this.setState({ loading: false });
    }
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;
    
    const formData = new FormData(form);
    const formDataObj = Object.fromEntries(formData.entries());
    
    const isValid = form.checkValidity();

    this.setState({ validated: true });

    if (isValid) {
      this.setState({ saving: true });

      try {
        const result = await apiService.saveRecord(formDataObj);

        if (result.success) {
          this.setState({ 
            showSuccessNotification: true,
            saving: false 
          });
        } else if (result.needsLogout && this.props.onLogout) {
          this.props.onLogout();
        } else {
          this.setState({ saving: false });
        }
      } catch (error) {
        this.setState({ saving: false });
      }
    } else {
      const firstInvalidField = form.querySelector(':invalid');
      if (firstInvalidField) {
        firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalidField.focus();
      }
    }
  };

  handleSuccessComplete = () => {
    this.setState({ showSuccessNotification: false });
    setTimeout(() => {
      this.props.onSuccess?.();
    }, 100);
  };

  getSingularModuleName = () => {
    const { module } = this.props;
    
    const authService = require('../../services/authService').default;
    const enabledModules = authService.getEnabledModules();
    
    if (enabledModules[module] && enabledModules[module].singular_modname) {
      return enabledModules[module].singular_modname;
    }
    
    return enabledModules[module]?.display_name || module;
  };

  render() {
    const { loading, validated, saving, crmFields, recordDetail, showSuccessNotification } = this.state;
    const { module, onCancel, crmid, entity_info } = this.props;

    if (loading) {
      return (
        <LoadingSpinner label="Loading form fields..." variant="fullscreen" />
      );
    }

    const isEdit = !!(
      crmid || 
      entity_info?.crmid || 
      entity_info?.id ||
      entity_info?.editMode
    );
    
    const recordLabel = entity_info?.label || recordDetail?.label || recordDetail?.name || 'Record';

    return (
      <>
        {/* Success Notification */}
        <ConfirmationNotification
          show={showSuccessNotification}
          message={`${isEdit ? 'Record updated' : 'Record created'} successfully!`}
          type="success"
          onComplete={this.handleSuccessComplete}
          duration={2500}
        />
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative">
        {saving && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50 rounded-lg">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-3"></div>
              <span className="text-sm text-gray-600 font-medium">Saving...</span>
            </div>
          </div>
        )}
        
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              {isEdit ? `Edit ${recordLabel}` : `Create ${this.getSingularModuleName()}`}
            </h2>
            <button
              type="button"
              onClick={onCancel}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={saving}
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={this.handleSubmit} className={`p-4 ${validated ? 'was-validated' : ''}`} noValidate>
          <input type="hidden" name="module" value={module} />
          
          {isEdit && (
            <>
              <input type="hidden" name="mode" value="edit" />
              <input type="hidden" name="record" value={crmid || entity_info?.crmid || recordDetail.id || recordDetail.record_id} />
              <input type="hidden" name="id" value={crmid || entity_info?.crmid || recordDetail.id || recordDetail.record_id} />
            </>
          )}

          <div className={`space-y-6 ${saving ? 'opacity-50 pointer-events-none' : ''}`}>
            {crmFields
              .filter(field => field.type !== 'selection' && field.type !== 'subject')
              .sort((a, b) => {
                const seqA = parseInt(a.sequence) || parseInt(a.sequencenumber) || 999;
                const seqB = parseInt(b.sequence) || parseInt(b.sequencenumber) || 999;
                return seqA - seqB;
              })
              .map((field, index) => (
                <FormField
                  key={index}
                  field={field}
                  data={recordDetail}
                  {...this.props}
                />
              ))}
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              type="submit"
              className="w-full text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center justify-center"
              style={{ backgroundColor: '#0075a8' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#005e86'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#0075a8'}
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>
      </div>
      </>
    );
  }
}

export default EntityForm;